import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db, rtdb } from '../firebase/config';
import {
    onAuthStateChanged,
    signInWithPopup,
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail,
    signOut as firebaseSignOut,
    updateProfile
} from 'firebase/auth';
import { doc, setDoc, onSnapshot, updateDoc, arrayUnion, arrayRemove, collection, addDoc, serverTimestamp as firestoreTimestamp, query, where, getDocs, limit } from 'firebase/firestore';
import { ref, onValue, set, onDisconnect, serverTimestamp } from 'firebase/database';
import { toast } from 'react-hot-toast';
import BatcheetLogo from '../components/BatcheetLogo';

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const safetyTimer = setTimeout(() => {
            setLoading(false);
        }, 1200);

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            clearTimeout(safetyTimer);
            setCurrentUser(user);
            if (user) {
                // Immediate fallback profile so UI doesn't freeze on initial load
                const baseName = user.displayName || user.email?.split('@')[0] || 'User';
                const cleanHandle = baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
                const defaultAvatar = user.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${user.uid}`;
                setUserProfile(prev => prev || {
                    displayName: baseName,
                    username: cleanHandle,
                    photoURL: defaultAvatar,
                    friends: [],
                    blockedUsers: []
                });
                setLoading(false);

                // Manage Realtime presence
                const userStatusDatabaseRef = ref(rtdb, '/status/' + user.uid);
                const isOfflineForDatabase = { state: 'offline', last_changed: serverTimestamp() };
                const isOnlineForDatabase = { state: 'online', last_changed: serverTimestamp() };
                const connectedRef = ref(rtdb, '.info/connected');

                onValue(connectedRef, (snapshot) => {
                    if (snapshot.val() === false) return;
                    onDisconnect(userStatusDatabaseRef).set(isOfflineForDatabase).then(() => {
                        set(userStatusDatabaseRef, isOnlineForDatabase);
                    });
                });

                // User profile listener
                const userRef = doc(db, 'users', user.uid);
                const unsubProfile = onSnapshot(userRef, (docSnap) => {
                    if (docSnap.exists()) {
                        setUserProfile(docSnap.data());
                    } else {
                        // Generate a clean handle from email or name
                        const initialHandle = baseName.toLowerCase().replace(/[^a-z0-9]/g, '') + Math.floor(100 + Math.random() * 900);
                        const initialProfile = {
                            displayName: baseName,
                            username: initialHandle,
                            photoURL: defaultAvatar,
                            bio: 'Exploring the 24h fleeting vibe',

                            blockedUsers: [],
                            friends: [],
                            currentNote: null,
                            createdAt: firestoreTimestamp(),
                            isProfileComplete: true
                        };
                        setDoc(userRef, initialProfile, { merge: true });
                        setUserProfile(initialProfile);
                    }
                    setLoading(false);
                }, (err) => {
                    console.error("Profile listen error:", err);
                    setLoading(false);
                });

                return () => {
                    unsubProfile();
                };
            } else {
                setUserProfile(null);
                setLoading(false);
            }
        });

        return () => {
            clearTimeout(safetyTimer);
            unsubscribe();
        };
    }, []);


    // Check if a username is available
    const checkUsernameAvailable = async (username) => {
        const cleanHandle = username.toLowerCase().trim().replace(/[^a-z0-9_]/g, '');
        if (cleanHandle.length < 3) return { available: false, error: 'Username must be at least 3 characters.' };
        const q = query(collection(db, 'users'), where('username', '==', cleanHandle), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
            const isSelf = snap.docs[0].id === currentUser?.uid;
            if (!isSelf) return { available: false, error: `@${cleanHandle} is already taken.` };
        }
        return { available: true, cleanHandle };
    };

    // Update Username
    const updateUsername = async (newUsername) => {
        if (!currentUser) return;
        const check = await checkUsernameAvailable(newUsername);
        if (!check.available) {
            toast.error(check.error);
            throw new Error(check.error);
        }
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { username: check.cleanHandle });
        toast.success(`Username updated to @${check.cleanHandle}!`);

        return check.cleanHandle;
    };

    // Email / Password Signup
    const signUpWithEmail = async (email, password, displayName, customUsername = '') => {
        let finalHandle = '';
        if (customUsername.trim()) {
            const check = await checkUsernameAvailable(customUsername);
            if (!check.available) {
                toast.error(check.error);
                throw new Error(check.error);
            }
            finalHandle = check.cleanHandle;
        } else {
            finalHandle = (displayName || email.split('@')[0]).toLowerCase().replace(/[^a-z0-9_]/g, '') + Math.floor(100 + Math.random() * 900);
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const photo = `https://api.dicebear.com/7.x/bottts/svg?seed=${user.uid}`;

        await updateProfile(user, {
            displayName: displayName || email.split('@')[0],
            photoURL: photo
        });

        const userRef = doc(db, 'users', user.uid);
        const profile = {
            displayName: displayName || email.split('@')[0],
            username: finalHandle,
            photoURL: photo,
            bio: 'Exploring the 24h fleeting vibe.',
            blockedUsers: [],
            friends: [],
            incomingFriendRequests: [],
            outgoingFriendRequests: [],
            currentNote: null,
            createdAt: firestoreTimestamp(),
            isProfileComplete: true
        };
        await setDoc(userRef, profile);

        // Send email verification
        try {
            await sendEmailVerification(user);
            toast.success("Verification email sent! Please check your inbox.", { duration: 6000 });
        } catch (verifErr) {
            console.warn("Could not send verification email:", verifErr);
        }

        return user;
    };

    // Email / Password Sign In (with seamless auto-provisioning for admin@batcheet.com)
    const signInWithEmail = async (email, password) => {
        try {
            return await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            if (email.trim().toLowerCase() === 'admin@batcheet.com') {
                try {
                    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                    const user = userCredential.user;
                    const userRef = doc(db, 'users', user.uid);
                    await setDoc(userRef, {
                        displayName: 'Admin',
                        username: 'admin',
                        photoURL: 'https://api.dicebear.com/7.x/bottts/svg?seed=admin_master',
                        bio: 'Batcheet Administrator 🛡️',
                        isAdmin: true,
                        friends: [],
                        blockedUsers: [],
                        currentNote: { text: 'System Operational ⚡', emoji: '🛡️', updatedAt: Date.now() },
                        createdAt: firestoreTimestamp(),
                        isProfileComplete: true
                    }, { merge: true });
                    return userCredential;
                } catch (createErr) {
                    throw err;
                }
            }
            throw err;
        }
    };

    // Google Sign In
    const signInWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        return await signInWithPopup(auth, provider);
    };

    // Resend Email Verification
    const resendVerificationEmail = async () => {
        if (!currentUser) return;
        try {
            await sendEmailVerification(currentUser);
            toast.success("Verification email resent! Check your inbox.");
        } catch (e) {
            console.error("Error resending email:", e);
            toast.error("Failed to resend. Please try again later.");
        }
    };

    // Forgot Password
    const resetPassword = async (email) => {
        return await sendPasswordResetEmail(auth, email);
    };

    // Block User
    const blockUser = async (userIdToBlock) => {
        if (!currentUser || !userIdToBlock) return;
        const userRef = doc(db, 'users', currentUser.uid);
        await setDoc(userRef, { blockedUsers: arrayUnion(userIdToBlock) }, { merge: true });
        toast.success("User blocked.");
    };

    // Unblock User
    const unblockUser = async (userIdToUnblock) => {
        if (!currentUser || !userIdToUnblock) return;
        const userRef = doc(db, 'users', currentUser.uid);
        await setDoc(userRef, { blockedUsers: arrayRemove(userIdToUnblock) }, { merge: true });
        toast.success("User unblocked.");
    };

    // Update Profile Details (Name, Bio, Avatar)
    const updateProfileDetails = async ({ displayName, bio, photoURL }) => {
        if (!currentUser) return;
        const updates = {};
        if (displayName !== undefined && displayName.trim()) {
            updates.displayName = displayName.trim();
        }
        if (bio !== undefined) {
            updates.bio = bio.trim().slice(0, 160);
        }
        if (photoURL !== undefined) {
            updates.photoURL = photoURL;
        }

        const userRef = doc(db, 'users', currentUser.uid);
        await setDoc(userRef, updates, { merge: true });

        if (updates.displayName) {
            await updateProfile(currentUser, { displayName: updates.displayName }).catch(() => {});
        }
        toast.success("Profile updated!");

    };

    // Send Friend Request
    const sendFriendRequest = async (targetUserId) => {
        if (!currentUser || !targetUserId || typeof targetUserId !== 'string' || currentUser.uid === targetUserId) {
            console.warn("Invalid friend request target:", targetUserId);
            return;
        }
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            const targetRef = doc(db, 'users', targetUserId);

            await setDoc(userRef, { outgoingFriendRequests: arrayUnion(targetUserId) }, { merge: true });
            await setDoc(targetRef, { incomingFriendRequests: arrayUnion(currentUser.uid) }, { merge: true });

            // Send notification to target user
            const notifRef = collection(db, 'users', targetUserId, 'notifications');
            await addDoc(notifRef, {
                type: 'friend_request',
                fromUserId: currentUser.uid,
                fromUserName: userProfile?.displayName || userProfile?.username || 'Someone',
                fromUserAvatar: userProfile?.photoURL || '',
                read: false,
                createdAt: firestoreTimestamp()
            });

            toast.success("Friend request sent!");
        } catch (err) {
            console.error("Send friend request error:", err);
            toast.error("Could not send friend request.");
        }
    };

    // Cancel Sent Friend Request
    const cancelFriendRequest = async (targetUserId) => {
        if (!currentUser || !targetUserId || typeof targetUserId !== 'string') return;
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            const targetRef = doc(db, 'users', targetUserId);

            await setDoc(userRef, { outgoingFriendRequests: arrayRemove(targetUserId) }, { merge: true });
            await setDoc(targetRef, { incomingFriendRequests: arrayRemove(currentUser.uid) }, { merge: true });
            toast.success("Friend request cancelled.");
        } catch (err) {
            console.error("Cancel friend request error:", err);
        }
    };

    // Accept Friend Request (Creates mutual friendship)
    const acceptFriendRequest = async (senderUserId) => {
        if (!currentUser || !senderUserId || typeof senderUserId !== 'string') return;
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            const senderRef = doc(db, 'users', senderUserId);

            // Add each other as friends & remove from requests
            await setDoc(userRef, {
                friends: arrayUnion(senderUserId),
                incomingFriendRequests: arrayRemove(senderUserId)
            }, { merge: true });
            await setDoc(senderRef, {
                friends: arrayUnion(currentUser.uid),
                outgoingFriendRequests: arrayRemove(currentUser.uid)
            }, { merge: true });

            // Notify the sender
            const notifRef = collection(db, 'users', senderUserId, 'notifications');
            await addDoc(notifRef, {
                type: 'friend_accept',
                fromUserId: currentUser.uid,
                fromUserName: userProfile?.displayName || userProfile?.username || 'Someone',
                fromUserAvatar: userProfile?.photoURL || '',
                read: false,
                createdAt: firestoreTimestamp()
            });

            toast.success("Friend request accepted! You are now friends 🎉");
        } catch (err) {
            console.error("Accept friend request error:", err);
            toast.error("Could not accept friend request.");
        }
    };

    // Decline Friend Request
    const declineFriendRequest = async (senderUserId) => {
        if (!currentUser || !senderUserId || typeof senderUserId !== 'string') return;
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            const senderRef = doc(db, 'users', senderUserId);

            await setDoc(userRef, { incomingFriendRequests: arrayRemove(senderUserId) }, { merge: true });
            await setDoc(senderRef, { outgoingFriendRequests: arrayRemove(currentUser.uid) }, { merge: true });
            toast.success("Request declined.");
        } catch (err) {
            console.error("Decline friend request error:", err);
        }
    };

    // Remove Friend (Mutual removal)
    const removeFriend = async (targetUserId) => {
        if (!currentUser || !targetUserId || typeof targetUserId !== 'string') return;
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            const targetRef = doc(db, 'users', targetUserId);

            await setDoc(userRef, { friends: arrayRemove(targetUserId) }, { merge: true });
            await setDoc(targetRef, { friends: arrayRemove(currentUser.uid) }, { merge: true });
            toast.success("Removed from friends.");
        } catch (err) {
            console.error("Remove friend error:", err);
            toast.error("Could not remove friend.");
        }
    };

    // Update Note (Instagram-style ephemeral text note)
    const updateNote = async (text, emoji = '💭') => {
        if (!currentUser) return;
        const userRef = doc(db, 'users', currentUser.uid);
        const noteData = text ? {
            text: text.trim().slice(0, 60),
            emoji: emoji || '💭',
            updatedAt: Date.now()
        } : null;

        await updateDoc(userRef, { currentNote: noteData });
        if (text) {
            toast.success("Thought shared! 🎈");
        } else {
            toast.success("Thought cleared.");
        }
    };

    // Sign out
    const signOut = async () => {
        if (currentUser) {
            const userStatusDatabaseRef = ref(rtdb, '/status/' + currentUser.uid);
            await set(userStatusDatabaseRef, { state: 'offline', last_changed: serverTimestamp() });
        }
        return await firebaseSignOut(auth);
    };

    const value = {
        currentUser,
        userProfile,
        signUpWithEmail,
        signInWithEmail,
        signInWithGoogle,
        resendVerificationEmail,
        resetPassword,
        blockUser,
        unblockUser,
        updateProfileDetails,
        sendFriendRequest,
        cancelFriendRequest,
        acceptFriendRequest,
        declineFriendRequest,
        removeFriend,
        updateNote,
        updateUsername,
        checkUsernameAvailable,
        signOut
    };

    if (loading) {
        return (
            <div className="bg-slate-50 dark:bg-[#0b0f17] flex flex-col items-center justify-center h-screen space-y-4 transition-colors">
                <div className="relative">
                    <div className="absolute inset-0 bg-sky-500/20 blur-2xl rounded-full animate-pulse" />
                    <BatcheetLogo size={56} className="relative z-10 animate-scale-in drop-shadow-md" />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-base font-bold tracking-tight text-slate-900 dark:text-white">Batcheet</span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20">
                        24H
                    </span>
                </div>
                <div className="w-24 h-1 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className="w-1/2 h-full bg-gradient-to-r from-sky-500 to-indigo-600 rounded-full animate-pulse" />
                </div>
            </div>
        );
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};